import React, { useState, useEffect } from "react";
import { Row, Col } from "reactstrap";
import { Flex, Spin, message, Modal } from "antd";
import { EditOutlined } from "@ant-design/icons";
import {
  GridComponent,
  Inject,
  ColumnDirective,
  ColumnsDirective,
  Page,
  Filter,
  Toolbar,
  ExcelExport,
  Sort,
  Resize,
  Group,
  CommandColumn,
} from "@syncfusion/ej2-react-grids";
import { apiRequest } from "../../../utilities/apiMethod";
import { RegisterForm } from "./RegisterForm";

const UserManagement = () => {
  // Grid State
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Form State
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    userId: 0,
    name: "",
    userName: "",
    password: "",
    email: "",
    employeeID: null,
    roleId: null,
    storeId: [],
    divisionId: [],
    departmentId: [],
    isLocked: false,
    isActive: false,
    approverRights: false,
  });

  const [errors, setErrors] = useState({
    name: "",
    userName: "",
    password: "",
    email: "",
    employeeID: "",
    roleId: "",
    storeId: "",
    divisionId: "",
    departmentId: "",
  });

  const [messageApi, contextHolder] = message.useMessage();

  // Dropdown Data State
  const [employeesData, setEmployeeData] = useState([]);
  const [storeData, setStoreData] = useState([]);
  const [roleData, setRoleData] = useState([]);
  const [divisionData, setDivisionData] = useState([]);
  const [departmentData, setDepartmentData] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        await Promise.all([
          fetchUsers(),
          fetchRoleData(),
          fetchStoreData(),
          fetchEmployeeData(),
          fetchDivisionData(),
        ]);
      } catch (e) {
        console.error("error while fetching", e);
      }
    };
    fetchAll();
  }, []);

  // Fetch departments when divisions change
  useEffect(() => {
    if (formData.divisionId && formData.divisionId.length > 0) {
      fetchDepartmentsForDivisions(formData.divisionId);
    } else {
      setDepartmentData([]);
      setFormData((prev) => ({ ...prev, departmentId: [] }));
    }
  }, [formData.divisionId]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await apiRequest("GET", "Users");

      if (response.IsSuccess && response.Data) {
        const formattedData = response.Data.map((item, index) => ({
          ...item,
          serialNo: index + 1,
        }));
        setData(formattedData);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoleData = async () => {
    try {
      const val = await apiRequest("GET", "Roles");

      if (
        val &&
        val.IsSuccess &&
        val.StatusCode === 200 &&
        Array.isArray(val.Data) &&
        val.Data.length > 0
      ) {
        const formattedData = val.Data.map((element, index) => ({
          ...element,
          serialNo: index + 1,
        }));
        setRoleData(formattedData);
      } else {
        setRoleData([]);
      }
    } catch (e) {
      setRoleData([]);
    }
  };

  const fetchStoreData = async () => {
    try {
      const val = await apiRequest("GET", "Store/getallstores");

      if (
        val &&
        val.IsSuccess &&
        val.StatusCode === 200 &&
        Array.isArray(val.Data) &&
        val.Data.length > 0
      ) {
        const formattedData = val.Data.map((element, index) => ({
          ...element,
          serialNo: index + 1,
        }));
        setStoreData(formattedData);
      } else {
        setStoreData([]);
      }
    } catch (e) {
      setStoreData([]);
    }
  };

  const fetchEmployeeData = async () => {
    try {
      const val = await apiRequest("GET", "Employee");

      if (
        val &&
        val.IsSuccess &&
        val.StatusCode === 200 &&
        Array.isArray(val.Data) &&
        val.Data.length > 0
      ) {
        const formattedData = val.Data.map((element, index) => ({
          ...element,
          serialNo: index + 1,
        }));
        setEmployeeData(formattedData);
      } else {
        setEmployeeData([]);
      }
    } catch (e) {
      setEmployeeData([]);
    }
  };

  const fetchDivisionData = async () => {
    try {
      const val = await apiRequest("GET", "Category/getalldivisions");

      if (
        val &&
        val.IsSuccess &&
        val.StatusCode === 200 &&
        Array.isArray(val.Data) &&
        val.Data.length > 0
      ) {
        setDivisionData(val.Data);
      } else {
        setDivisionData([]);
      }
    } catch (e) {
      setDivisionData([]);
      console.error("Error fetching divisions:", e);
    }
  };

  const fetchDepartmentsForDivisions = async (divisionIds) => {
    try {
      setLoadingDepartments(true);

      const promises = divisionIds.map((divisionId) =>
        apiRequest("GET", `Category/getAllDepartmentsByDivision/${divisionId}`)
      );

      const results = await Promise.all(promises);

      let allDepartments = [];
      results.forEach((result) => {
        if (
          result &&
          result.IsSuccess &&
          result.StatusCode === 200 &&
          Array.isArray(result.Data)
        ) {
          allDepartments = [...allDepartments, ...result.Data];
        }
      });

      const uniqueDepartments = allDepartments.filter(
        (dept, index, self) =>
          index === self.findIndex((d) => d.Department === dept.Department)
      );

      setDepartmentData(uniqueDepartments);
    } catch (e) {
      setDepartmentData([]);
      console.error("Error fetching departments:", e);
      messageApi.error("Failed to fetch departments");
    } finally {
      setLoadingDepartments(false);
    }
  };

  const handleChange = (key, value) => {
    if (key === "storeId") {
      if (value.includes("all")) {
        if (!formData?.storeId?.includes("all")) {
          const allStoreIds = storeData.map((store) => store.StoreCode);
          setFormData((prev) => ({
            ...prev,
            storeId: allStoreIds,
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            storeId: [],
          }));
        }
      } else {
        setFormData((prev) => ({ ...prev, storeId: value }));
      }
    } else if (key === "divisionId") {
      if (value.includes("all")) {
        if (!formData?.divisionId?.includes("all")) {
          const allDivisionIds = divisionData.map((div) => div.Division);
          setFormData((prev) => ({
            ...prev,
            divisionId: allDivisionIds,
            departmentId: [],
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            divisionId: [],
            departmentId: [],
          }));
        }
      } else {
        setFormData((prev) => ({
          ...prev,
          divisionId: key === "userName" ? value.trim() : value,
          departmentId: [],
        }));
      }
    } else if (key === "departmentId") {
      if (value.includes("all")) {
        if (!formData?.departmentId?.includes("all")) {
          const allDepartmentIds = departmentData.map(
            (dept) => dept.Department
          );
          setFormData((prev) => ({
            ...prev,
            departmentId: allDepartmentIds,
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            departmentId: [],
          }));
        }
      } else {
        setFormData((prev) => ({ ...prev, departmentId: value }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [key]: key === "userName" ? value.trim() : value,
      }));
    }

    if (errors[key]) {
      setErrors((prev) => ({
        ...prev,
        [key]: "",
      }));
    }
  };

  const handleReset = () => {
    setFormData({
      userId: 0,
      name: "",
      userName: "",
      password: "",
      email: "",
      employeeID: null,
      roleId: null,
      storeId: [],
      divisionId: [],
      departmentId: [],
      isLocked: false,
      isActive: false,
      approverRights: false,
    });

    setErrors({
      name: "",
      userName: "",
      password: "",
      email: "",
      employeeID: "",
      roleId: "",
      storeId: "",
      divisionId: "",
      departmentId: "",
    });

    setDepartmentData([]);
  };

  const validateFormValues = () => {
    const newErrors = {
      name: "",
      userName: "",
      password: "",
      email: "",
      employeeID: "",
      roleId: "",
      storeId: "",
      divisionId: "",
      departmentId: "",
    };

    let isValid = true;

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
    }

    if (!formData.userName.trim()) {
      newErrors.userName = "User Name is required";
      isValid = false;
    }

    // Only validate password for new users
    if (formData.userId === 0) {
      if (!formData.password.trim()) {
        newErrors.password = "Password is required";
        isValid = false;
      } else if (formData.password.length < 6) {
        newErrors.password = "Password must be at least 6 characters";
        isValid = false;
      }
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = "Invalid email format";
        isValid = false;
      }
    }

    if (!formData.employeeID) {
      newErrors.employeeID = "Employee selection is required";
      isValid = false;
    }

    if (!formData.roleId) {
      newErrors.roleId = "Role is required";
      isValid = false;
    }

    if (Array.isArray(formData.storeId) && formData.storeId.length <= 0) {
      newErrors.storeId = "Store selection is required";
      isValid = false;
    }

    if (Array.isArray(formData.divisionId) && formData.divisionId.length <= 0) {
      newErrors.divisionId = "Division selection is required";
      isValid = false;
    }

    if (
      Array.isArray(formData.departmentId) &&
      formData.departmentId.length <= 0
    ) {
      newErrors.departmentId = "Department selection is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSave = async () => {
    if (!validateFormValues()) {
      messageApi.open({
        type: "error",
        content: "Please fill in all required fields correctly",
      });
      return;
    }

    const payload = {
      name: formData.name,
      userName: formData.userName,
      password: formData.password,
      email: formData.email,
      employeeID: formData.employeeID,
      roleId: formData.roleId,
      storeId:
        Array.isArray(formData.storeId) && formData.storeId.length > 0
          ? formData.storeId.join(",")
          : "",
      division:
        Array.isArray(formData.divisionId) && formData.divisionId.length > 0
          ? formData.divisionId.join(",")
          : "",
      department:
        Array.isArray(formData.departmentId) && formData.departmentId.length > 0
          ? formData.departmentId.join(",")
          : "",
      isLocked: formData.isLocked,
      isActive: formData.isActive,
      approverRights: formData.approverRights,
    };

    const isUpdate = formData.userId !== 0;
    const url = isUpdate ? `Users/${formData.userId}` : `Users/Register`;
    const method = isUpdate ? "PUT" : "POST";

    try {
      const result = await apiRequest(method, url, payload);
      if (!result.IsSuccess) {
        throw new Error(
          result.DisplayMessage || `HTTP error! status: ${result.StatusCode}`
        );
      }
      messageApi.open({
        type: "success",
        content:
          result.DisplayMessage ||
          (isUpdate
            ? "User updated successfully"
            : "User created successfully"),
      });
      setTimeout(() => {
        handleReset();
        setIsModalOpen(false);
        fetchUsers(); // Refresh the grid
      }, 1000);
    } catch (error) {
      console.error("Error saving User:", error);
      messageApi.open({
        type: "error",
        content:
          error.message ||
          (isUpdate ? "Failed to update User" : "Failed to create User"),
      });
    }
  };

  const handleEdit = (rowData) => {
    setIsEditMode(true);

    // Parse comma-separated strings back to arrays
    const storeIds = rowData.StoreId ? rowData.StoreId.split(",") : [];
    const divisionIds = rowData.Division ? rowData.Division.split(",") : [];
    const departmentIds = rowData.Department
      ? rowData.Department.split(",")
      : [];

    setFormData({
      userId: rowData.UserLoginId,
      name: rowData.Name || "",
      userName: rowData.UserName || "",
      password: "", // Don't populate password for security
      email: rowData.Email || "",
      employeeID: rowData.EmployeeID || null,
      roleId: rowData.RoleId || null,
      storeId: storeIds,
      divisionId: divisionIds,
      departmentId: departmentIds,
      isLocked: rowData.IsLocked || false,
      isActive: rowData.IsActive || false,
      approverRights: rowData.ApproverRights || false,
    });

    setIsModalOpen(true);
  };

  const handleAddNew = () => {
    setIsEditMode(false);
    handleReset();
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    handleReset();
  };

  const onRowSelected = (args) => {
    if (args.target && args.target.classList.contains("edit-icon")) {
      handleEdit(args.data);
    }
  };

  const editTemplate = (props) => {
    return (
      <div
        className="edit-icon"
        style={{
          cursor: "pointer",
          color: "#1890ff",
          fontSize: "16px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
        }}
        title="Edit User"
      >
        <EditOutlined />
      </div>
    );
  };

  if (loading) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Flex align="center" gap="middle">
          <Spin size="large" />
        </Flex>
      </div>
    );
  }

  const commmandTemplate = [
    {
      type: "Edit",
      buttonOption: { cssClass: "e-flat", iconCss: "e-edit e-icons" },
    },
  ];

  function findIndexByObjectMatch(masterArray, searchObject) {
    for (let i = 0; i < masterArray.length; i++) {
      const currentObject = masterArray[i];

      if (isObjectMatch(currentObject, searchObject)) {
        return i;
      }
    }

    return -1; // Return -1 if no match is found
  }
  function isObjectMatch(obj1, obj2) {
    if (typeof obj1 !== "object" || typeof obj2 !== "object") {
      return false;
    }

    for (const key in obj2) {
      if (obj2.hasOwnProperty(key)) {
        if (!obj1.hasOwnProperty(key) || obj1[key] !== obj2[key]) {
          return false;
        }
      }
    }

    return true;
  }

  const prepareEditData = (args) => {
    // let newData = [args.rowIndex, args.rowData];
    const index = findIndexByObjectMatch(data, args.rowData);

    handleUpdate(args.rowData);
  };

  const handleUpdate = (rowData) => {
    const storeIds = rowData.Store
      ? rowData.Store.split(",").map((s) => s.trim())
      : [];
    const divisionIds = rowData.Division
      ? rowData.Division.split(",").map((s) => s.trim())
      : [];
    const departmentIds = rowData.Department
      ? rowData.Department.split(",").map((s) => s.trim())
      : [];

    setFormData({
      userId: rowData.UserLoginId || 0,
      name: rowData.Name || "",
      userName: rowData.UserName || "",
      password: "", // Don't populate password for security
      email: rowData.Email || "",
      employeeID: rowData.EmployeeID || null,
      roleId: rowData.RoleId || null,
      storeId: storeIds,
      divisionId: divisionIds,
      departmentId: departmentIds,
      isLocked: rowData.IsLocked || false,
      isActive: rowData.IsActive || false,
      approverRights: rowData.ApproverRights || false,
    });

    setIsModalOpen(true);
  };

  return (
    <>
      {contextHolder}
      <div
        className={`grid_compact ${isMobile ? "e-bigger" : ""}`}
        style={{
          position: "relative",
          marginTop: "10px",
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          padding: "6px",
          marginBottom: "20px",
        }}
      >
        <Row>
          <Col>
            <GridComponent
              id="User_List_GridID"
              dataSource={data}
              allowPaging={true}
              allowSorting={true}
              allowFiltering={true}
              filterSettings={{ type: "Excel" }}
              toolbar={["Search"]}
              enableAdaptiveUI={isMobile}
              rowRenderingMode={isMobile ? "Vertical" : "Horizontal"}
              gridLines="Both"
              height={"400px"}
              pageSettings={{
                pageSizes: [10, 20, 30, 40, 50, "All"],
                pageCount: 10,
              }}
              rowHeight={isMobile ? "" : 32}
              commandClick={(args) => prepareEditData(args)}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field="serialNo"
                  headerText="S No"
                  width="80"
                  textAlign="Center"
                  allowFiltering={false}
                />
                <ColumnDirective
                  field="UserLoginId"
                  headerText="ID"
                  visible={false}
                  isPrimaryKey={true}
                />
                  <ColumnDirective
                  field="Name"
                  headerText="Name"
                 width="150"
                  textAlign="Left"
                />
                <ColumnDirective
                  field="UserName"
                  headerText="User Name"
                  width="150"
                  textAlign="Left"
                />
                <ColumnDirective
                  field="Email"
                  headerText="Email"
                  width="200"
                  textAlign="Left"
                />
                <ColumnDirective
                  field="RoleName"
                  headerText="Role Name"
                  width="200"
                  textAlign="Center"
                />
                <ColumnDirective
                  field="IsActive"
                  displayAsCheckBox={true}
                  headerText="Active"
                  width="100"
                  textAlign="Center"
                />
                {/* <ColumnDirective
                  field="IsLocked"
                  displayAsCheckBox={true}
                  headerText="Locked"
                  width="100"
                  textAlign="Center"
                /> */}
                <ColumnDirective
                  headerText="Edit"
                  commands={commmandTemplate}
                  visible={true}
                  width={"150"}
                  textAlign="center"
                  //   freeze="Right"
                />
              </ColumnsDirective>
              <Inject
                services={[
                  Page,
                  Filter,
                  Toolbar,
                  ExcelExport,
                  Sort,
                  Group,
                  Resize,
                  CommandColumn,
                ]}
              />
            </GridComponent>
          </Col>
        </Row>
      </div>

      <Modal
        title={isEditMode ? "Edit User" : "Add New User"}
        open={isModalOpen}
        onCancel={handleModalClose}
        footer={null}
        width={1200}
        destroyOnClose
      >
        <RegisterForm
          formData={formData}
          handleChange={handleChange}
          showPassword={showPassword}
          toggleShow={() => setShowPassword(!showPassword)}
          errors={errors}
          handleReset={handleReset}
          handleSave={handleSave}
          employeesData={employeesData}
          storeData={storeData}
          roleData={roleData}
          divisionData={divisionData}
          departmentData={departmentData}
          loadingDepartments={loadingDepartments}
        />
      </Modal>
    </>
  );
};

export default UserManagement;
